import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../components/ConfirmModal';
import { useNavigate, useParams } from 'react-router-dom';
import cursoService from '../../services/cursoService';
import { useAuth } from '../../contexts/AuthContext';

const initialState = {
  nome: '',
  padraoMatricula: '',
  proximoNumeroMatricula: 1
};

const CursoEdit = () => {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCurso();
  }, [id]);

  const loadCurso = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!id || id === 'undefined') {
        setError('ID do curso inválido');
        return;
      }
      
      const response = await cursoService.getById(id);
      
      if (response.success) {
        const curso = response.data;
        setForm({
          nome: curso.nome || '',
          padraoMatricula: curso.padraoMatricula || '',
          proximoNumeroMatricula: curso.proximoNumeroMatricula || 1
        });
      } else {
        setError('Curso não encontrado');
      }
    } catch (err) {
      console.error('Erro ao carregar curso:', err);
      setError(err.message || 'Erro ao carregar curso');
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    if (!form || !form.nome || !form.nome.trim()) return 'O nome do curso é obrigatório.';
    if (form.nome.length > 255) return 'O nome deve ter até 255 caracteres.';
    return '';
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmUpdate = async () => {
    setShowConfirm(false);
    setSaving(true);
    setError('');

    try {
      console.log('Form state:', form); // Log temporário para debug
      
      // Verificar se form e nome existem
      if (!form || !form.nome) {
        setError('Dados do formulário inválidos. Tente recarregar a página.');
        setSaving(false);
        return;
      }

      // Preparar dados para envio
      const cursoData = {
        nome: form.nome.trim(),
        padraoMatricula: form.padraoMatricula?.trim() || null,
        proximoNumeroMatricula: form.proximoNumeroMatricula || 1
      };

      if (!cursoData.nome) {
        setError('O nome do curso é obrigatório');
        setSaving(false);
        return;
      }

      const response = await cursoService.update(id, cursoData);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate('/cursos'), 1200);
      } else {
        setError(response.message || 'Erro ao atualizar curso');
      }
    } catch (err) {
      console.error('Erro ao atualizar curso:', err);
      // A mensagem de erro já vem tratada do service
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);

    try {
      const response = await cursoService.delete(id);

      if (response.success) {
        setDeleteSuccess(true);
        setTimeout(() => navigate('/cursos'), 1200);
      } else {
        setError(response.message || 'Erro ao deletar curso');
      }
    } catch (err) {
      console.error('Erro ao deletar curso:', err);
      setError(err.message || 'Erro ao deletar curso');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="max-w-lg mx-auto mt-10 text-blue-600">Carregando dados...</div>;
  if (deleteSuccess) return <div className="max-w-lg mx-auto mt-10 text-green-600">Curso excluído com sucesso!</div>;
  if (!hasPermission('cursos.update')) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-600">Acesso negado: você não tem permissão para editar cursos.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Curso</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            maxLength={255}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Nome do curso"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Padrão de Matrícula
              <span className="text-gray-500 text-xs ml-2">(Use #### para os números)</span>
            </label>
            <input
              type="text"
              name="padraoMatricula"
              value={form.padraoMatricula}
              onChange={handleChange}
              maxLength={20}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="Ex: CPL####, TBS####"
            />
            <p className="text-xs text-gray-500 mt-1">
              Deixe vazio para usar numeração sequencial simples
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Próximo Número</label>
            <input
              type="number"
              name="proximoNumeroMatricula"
              value={form.proximoNumeroMatricula}
              onChange={handleChange}
              min="1"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Próximo número da sequência de matrícula
            </p>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {saving && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="animate-spin h-5 w-5 text-blue-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">Salvando alterações...</p>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">Curso atualizado com sucesso!</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={() => navigate('/cursos')}
            disabled={saving || deleting}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button 
            type="submit" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
            disabled={saving || deleting}
           aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={handleDelete} 
            disabled={saving || deleting}
           aria-label="Excluir">
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
          </button>
        </div>
      </form>
      
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Alteração"
        message="Deseja realmente salvar as alterações neste curso?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirmUpdate}
      />
      
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message="Deseja realmente excluir este curso? Esta ação não pode ser desfeita."
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default CursoEdit;
